import { ContentBody } from "@app/components/core/ContentBody/ContentBody"
import { ContentFooter } from "@app/components/core/ContentFooter/ContentFooter"
import { ContentHeader } from "@app/components/core/ContentHeader/ContentHeader"
import { SelectionEntry } from "@app/components/core/SelectionEntry/SelectionEntry"
import Input from "@app/components/move2Library/Input/Input"
import { useCustomNavigation } from "@app/components/navigation/NavigationHandler"
import OrderSubPage from "@app/components/pageTypes/OrderSubPage/OrderSubPage"
import { AnimationTypes } from "@app/components/routing/AnimatedRoute/AnimatedRoute"
import { useRaiseError } from "@app/store/actions/errorActions"
import { useGetFutureOrders, useGetPlanningObject, useGetVisitorKinds, useSavePlanningObject } from "@app/store/service/connectorService"
import { ORDER_LINE_TYPES, OrderErrors } from "@app/utils/constants"
import { getTimeString } from "@app/utils/localeUtils"
import { emptyArray } from "@app/utils/objectUtils"
import { faSpinner } from "@fortawesome/free-solid-svg-icons/faSpinner"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { classNamez } from "@tecreation/funnel-components"
import { ResponseError } from "@tecreation/js-state-react"
import { useCallback, useEffect, useReducer, useState } from "react"

import { MultilineText } from "@app/components/core/MultilineText/MultilineText"
import { setZoneId } from "@app/store/actions/coreActions"
import { addVisitorKind, setSelectedDay } from "@app/store/actions/orderActions"
import { useGetStrings } from "@app/store/service/cmsService"
import { appStore } from "@app/store/store"
import { useForm } from "react-hook-form"
import "./SaunaBoat.css"

export default function SaunaBoat() {
	const [state, dispatch] = useReducer(reducer, {
		date: new Date(),
		option: undefined,
		quantity: 0,
		order: undefined,
		ticketTimes: undefined,
		didSetFirstDate: undefined,
		names: [],
	})

	const raiseError = useRaiseError()
	const { replace } = useCustomNavigation()
	const [succeed, setSucceed] = useState(false)
	const {
		data: {
			saunaBoatTitle,
			labelDate,
			saunaBoatPersonTitle,
			labelName,
			saunaBoatNoTicketInfo,
			saunaBoatNoCapacity,
			buttonBookTicket,
			saunaBoatSucceedText,
			errorInputRequiredField,
		},
	} = useGetStrings(undefined, { defaultData: {} })
	const [saveOption, { isLoading: saveOptionLoading }] = useSavePlanningObject()
	const { data: visitorKinds, isLoading: visitorKindLoading } = useGetVisitorKinds({ zoneId: 1, locationId: appStore.get("core.locationId") })

	const {
		register,
		formState: { errors },
		handleSubmit,
	} = useForm({})

	const { data: futureOrders, isLoading: historyLoading } = useGetFutureOrders()
	const { data: planningData, isFetching } = useGetPlanningObject(
		{ date: state.date ? state.date.toISOString().slice(0, 10) : undefined },
		{
			skip: false,
			defaultData: [],
		},
	)

	useEffect(() => {
		if (futureOrders && state.date && futureOrders.length > 0) {
			if (!state.didSetFirstDate) {
				let earliestOrder = futureOrders.reduce(function (prev, current) {
					return prev.date < current.date ? prev : current
				})
				dispatch({ type: "SET_FIRST_DATE", payload: new Date(earliestOrder.date) })
			} else {
				let dateString = state.date.toISOString().slice(0, 10)
				let order = futureOrders.find(e => e.date === dateString)
				dispatch({ type: "SET_ORDER", payload: order })
			}
		}
	}, [futureOrders, state.date, state.didSetFirstDate])

	const onNext = useCallback(async () => {
		handleSubmit(async data => {
			let names = []
			Object.keys(data).map((e, i) => {
				let v = data[e]
				if (v && v.length > 0 && i < state.quantity) {
					// we ignore the names that was inserted before the customer reduced the quantity
					names.push(v)
				}
			})

			try {
				await saveOption({
					planningObject: {
						...state.option,
						date: state.date.toISOString().slice(0, 10),
						quantity: state.quantity,
						names,
					},
				}).unwrap()

				if (!state.order) {
					emptyArray(state.quantity).forEach(() => {
						addVisitorKind(visitorKinds[0])
					})
					setZoneId(3)
					setSelectedDay({ dateString: state.date.toISOString().slice(0, 10), date: state.date, dateObj: state.date, reload: true })
					replace("/order/date-selection")
				} else {
					setSucceed(true)
				}
			} catch (e) {
				if (e instanceof ResponseError) {
					if (e.response.data && e.response.data.errorMessage && e.response.data.errorMessage.startsWith("wrn.PlanningObject.AlreadyExists")) {
						return raiseError(e, undefined)
					}
				}
				raiseError(e, undefined, true)
			}
		})()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [saveOption, state])

	if (historyLoading || visitorKindLoading) {
		return "loading"
	}

	if (succeed) {
		return (
			<OrderSubPage>
				<ContentBody className="flex-1 flex flex-column pt-2 flex-center" scrollable>
					<div className="color-bg-green-100 p-3">
						<span className="color-fg-green-900">
							<MultilineText>{saunaBoatSucceedText}</MultilineText>
						</span>
					</div>
				</ContentBody>
			</OrderSubPage>
		)
	}

	return (
		<OrderSubPage>
			<ContentHeader colorStyle="white" canGoBack animation={AnimationTypes.slide}>
				{saunaBoatTitle}
			</ContentHeader>
			<ContentBody className="flex-1 flex flex-column pt-2" scrollable>
				<div>
					<Input
						className="picker"
						type="date"
						label={labelDate}
						onChange={e => {
							let d = new Date(e.target.value)
							if (!isNaN(d.getTime())) {
								dispatch({ type: "SET_DATE", payload: d })
							}
						}}
						value={state.date.toISOString().slice(0, 10)}
						onKeyPress={e => e.preventDefault()}
					/>
				</div>
				<div className="mt-6">
					{isFetching && (
						<div className="w-100 h-100 flex flex-center f00">
							<FontAwesomeIcon icon={faSpinner} spin />
						</div>
					)}
					{!isFetching && planningData && planningData.length === 0 && <div className="color-fg-danger f3 text-center">{saunaBoatNoCapacity}</div>}
					{!isFetching && planningData && planningData.length > 0}
					<div>
						{planningData
							.filter(e => {
								let isValid = state.ticketTimes ? e.timeFrom >= state.ticketTimes.from && e.timeTo <= state.ticketTimes.to : true
								return isValid
							})
							.map(data => {
								let split1 = data.timeFrom.split(":")
								let split2 = data.timeTo.split(":")
								let from = new Date(state.date)
								from.setHours(split1[0])
								from.setMinutes(split1[1])
								from.setSeconds(split1[2])
								let to = new Date(state.date)
								to.setHours(split2[0])
								to.setMinutes(split2[1])
								to.setSeconds(split2[2])
								return (
									<div
										key={data.timeBlockPlanningId}
										className={
											"border rounded-3 color-border-primary color-fg-primary px-4 py-2 mb-3 clickable saunaBoatEntry flex flex-justify-between" +
											classNamez({ selected: state.option && data.timeBlockPlanningId === state.option.timeBlockPlanningId })
										}
										onClick={() => {
											dispatch({ type: "SET_OPTION", payload: data })
										}}
									>
										<div>
											{getTimeString(from)} - {getTimeString(to)}
										</div>
										<div>Freie Plätze: {data.restCount}</div>
									</div>
								)
							})}
					</div>
				</div>

				{!isFetching && (
					<div className="mt-4 mb-4">
						<SelectionEntry
							count={state.quantity}
							onAdd={() => {
								dispatch({ type: "SET_QUANITITY", payload: state.quantity + 1 })
							}}
							onRemove={() => {
								dispatch({ type: "SET_QUANITITY", payload: state.quantity - 1 })
							}}
							plusDisabled={state.quantity >= (state.option ? state.option.restCount : 0)}
						>
							<span className="f2">{saunaBoatPersonTitle}</span>
						</SelectionEntry>
					</div>
				)}

				{state.quantity && state.quantity > 0 ? (
					<>
						{emptyArray(state.quantity).map((__, i) => {
							return (
								<Input
									label={labelName}
									className={"mt-3" + classNamez({ "mt-0": i == 0, "mb-3": i === state.quantity - 1 })}
									key={i}
									formRegister={register("name" + i, {
										required: {
											value: true,
											message: errorInputRequiredField,
										},
									})}
									errors={errors}
									onChange={e => {
										dispatch({ type: "SET_NAME", payload: { index: i, value: e.target.value } })
									}}
								/>
							)
						})}
					</>
				) : null}

				{!state.order && (
					<div className="text-center mt-auto color-fg-primary">
						<MultilineText>{saunaBoatNoTicketInfo}</MultilineText>
					</div>
				)}
			</ContentBody>
			<ContentFooter
				buttonProps={{
					text: buttonBookTicket,
					disabled: !state.option || !state.quantity,
					onNext: onNext,
					disabledReason: state.option ? OrderErrors.noSelectedPersons : "",
					isLoading: saveOptionLoading,
				}}
			/>
		</OrderSubPage>
	)
}

function reducer(state, { type, payload }) {
	if (type === "SET_FIRST_DATE") {
		return { ...state, didSetFirstDate: true, date: payload }
	} else if (type === "SET_QUANITITY") {
		let nextNames = emptyArray(payload)
		state.names.forEach((name, i) => {
			if (i < nextNames.length) {
				nextNames[i] = name
			}
		})
		return { ...state, quantity: payload, names: nextNames }
	} else if (type === "SET_OPTION") {
		return { ...state, option: payload, quantity: state.quantity > payload.restCount ? payload.restCount : state.quantity }
	} else if (type === "SET_DATE") {
		return { ...state, date: payload, option: undefined, quantity: 0 }
	} else if (type === "SET_NAME") {
		let names = [...state.names]
		names[payload.index] = payload.value
		return { ...state, names }
	} else if (type === "SET_ORDER") {
		let order = payload

		let nextTicketTimes = undefined
		let newQuantity = 0
		if (order) {
			order.orderLines.forEach(e => {
				if (e.typeId === ORDER_LINE_TYPES.Ticket) {
					e.details.forEach(d => {
						if (d.typeId === ORDER_LINE_TYPES.Ticket) {
							if (d.zoneId !== undefined) {
								newQuantity += d.quantity

								if (!nextTicketTimes && d.timeFrom && d.timeTo) {
									nextTicketTimes = {
										from: d.timeFrom,
										to: d.timeTo,
									}
								}
							}
						}
					})
				}
			})
		}

		return { ...state, order, quantity: newQuantity, ticketTimes: nextTicketTimes }
	}

	return state
}
